// views/Settings/SupportSlaSettings.js
// First-response and resolution targets per case priority, in minutes. The
// service (services/supportCaseService.js) had a writer since the support
// cases build and no route or screen called it until the Settings menu.
// How each target works is in help/supportHelp.js (1 Nov 2026).
import React, { useEffect, useState } from "react";
import { notify } from "../../utilities/notify";
import SettingsPanel from "../../components/SettingsPanel";
import { THEME as C } from "../../components/dashboardTheme";
import { apiGetSupportSla, apiUpdateSupportSla } from "../../api";
import { field, LastChanged, ReadOnlyNotice, SaveButton } from "./settingsUi";
import SettingHelp from "./settingsHelp";

const PRIORITIES = ["URGENT", "HIGH", "NORMAL", "LOW"];

// Column headings, each with the tooltip that says what the column does.
const COLUMNS = [
  { label: "Priority", help: <SettingHelp topic="supportCasePriority" /> },
  { label: "First reply within (minutes)", help: <SettingHelp topic="supportSlaFirstResponse" /> },
  { label: "Resolved within (minutes)", help: <SettingHelp topic="supportSlaResolution" /> },
  { label: "Shipped default", help: null },
];
const fmtMinutes = (m) => {
  const n = Number(m);
  if (!Number.isFinite(n)) return "";
  if (n % 1440 === 0) return `${n / 1440} day${n / 1440 === 1 ? "" : "s"}`;
  if (n % 60 === 0) return `${n / 60} hour${n / 60 === 1 ? "" : "s"}`;
  return `${n} min`;
};

const SupportSlaSettings = ({ canEdit = true }) => {
  const [sla, setSla] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);

  // The form mirrors a set of targets; "live" is what the server holds, so
  // Save stays off until the form differs from it.
  const fillForm = (targets) => setForm(Object.fromEntries(PRIORITIES.map((p) => [p, {
    firstResponse: String(targets?.[p]?.firstResponse ?? ""),
    resolution: String(targets?.[p]?.resolution ?? ""),
  }])));
  const applyLive = (live) => { setSla(live); fillForm(live); };

  useEffect(() => {
    apiGetSupportSla()
      .then(({ data }) => { applyLive(data.sla); setDefaults(data.defaults); })
      .catch((err) => notify.fromError(err, "Could not load the support SLA"))
      .finally(() => setLoading(false));
  }, []);

  const set = (p, k, v) => setForm((f) => ({ ...f, [p]: { ...f[p], [k]: v } }));
  const parsed = Object.fromEntries(PRIORITIES.map((p) => [p, {
    firstResponse: Number(form[p]?.firstResponse), resolution: Number(form[p]?.resolution),
  }]));
  const valid = PRIORITIES.every((p) =>
    Number.isFinite(parsed[p].firstResponse) && parsed[p].firstResponse > 0 &&
    Number.isFinite(parsed[p].resolution) && parsed[p].resolution >= parsed[p].firstResponse);
  const unchanged = sla && PRIORITIES.every((p) =>
    parsed[p].firstResponse === Number(sla[p]?.firstResponse) && parsed[p].resolution === Number(sla[p]?.resolution));

  const save = async (e) => {
    e.preventDefault();
    if (!valid) return notify.error("Check the targets", "Each priority needs a first reply target above 0 and a resolution target no shorter than it.");
    setSaving(true);
    try {
      const { data } = await apiUpdateSupportSla(parsed);
      applyLive(data.sla);
      setSavedAt(Date.now());
      notify.success("Support targets saved", "New and reopened cases get these targets. Cases already open keep the deadlines they were given.");
    } catch (err) {
      notify.fromError(err, "Could not save the support SLA");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPanel icon="fa-headset" title="Support case SLA" subtitle={loading ? "Loading..." : "How fast support must first reply to, and then resolve, a case of each priority"}>
      {!canEdit && <ReadOnlyNotice level="update" />}
      <form onSubmit={save}>
        <SettingsPanel.Section title={<>Targets<SettingHelp topic="supportCaseSla" /></>} icon="fa-stopwatch">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <th key={col.label} style={{ textAlign: "left", fontSize: 11, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.06em", padding: "6px 8px", borderBottom: `1px solid ${C.border}` }}>{col.label}{col.help}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PRIORITIES.map((p) => (
                <tr key={p}>
                  <td style={{ padding: "8px", fontWeight: 600, color: C.text }}>{p.charAt(0) + p.slice(1).toLowerCase()}</td>
                  <td style={{ padding: "8px" }}>
                    <input style={field.input} type="number" min="1" value={form[p]?.firstResponse ?? ""} disabled={!canEdit || saving || loading}
                      onChange={(e) => set(p, "firstResponse", e.target.value)} />
                    <div style={field.help}>{fmtMinutes(form[p]?.firstResponse)}</div>
                  </td>
                  <td style={{ padding: "8px" }}>
                    <input style={field.input} type="number" min="1" value={form[p]?.resolution ?? ""} disabled={!canEdit || saving || loading}
                      onChange={(e) => set(p, "resolution", e.target.value)} />
                    <div style={field.help}>{fmtMinutes(form[p]?.resolution)}</div>
                  </td>
                  <td style={{ padding: "8px", color: C.textLight, fontSize: 12 }}>
                    {defaults?.[p] ? `${fmtMinutes(defaults[p].firstResponse)} / ${fmtMinutes(defaults[p].resolution)}` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={field.actions}>
            {canEdit && <SaveButton saving={saving} disabled={!valid || unchanged || loading} />}
            {canEdit && defaults && (
              <button type="button" onClick={() => fillForm(defaults)} disabled={saving}
                style={{ background: "none", border: "none", color: C.blue, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0 }}>
                Use the shipped defaults
              </button>
            )}
            <div style={{ marginLeft: "auto" }}><LastChanged keys={["support_case_sla"]} refreshToken={savedAt} /></div>
          </div>
        </SettingsPanel.Section>
      </form>
    </SettingsPanel>
  );
};

export default SupportSlaSettings;
